import React, { useState, useEffect, useRef, forwardRef } from "react";
import clsx from 'clsx';
import {
  Card, CardContent,
} from '@material-ui/core';
import { withStyles } from '@material-ui/styles';

import withScrollListener from "../../../../../hocs/with_scroll_listener.js.jsx";
import { compose } from 'redux';

import I18n from "../../../../../base/i18n.js.jsx";
import { itemWithKeys } from "../../../../../utils/utils.js.jsx";
import FormFields from "../../../../../base/form/form_fields.js.jsx";

import { DownloadableModel } from "../../../../../models/downloadables/downloadable_model.js.jsx";

import NavigationTabContent from "../../../components/navigation_tab_content.js.jsx";

import pageStyles from "../../../../styles/page_styles.js.jsx";
import { infoPageStyles } from "../../../base_info_page.js.jsx";

const styles = theme => ({
  developerDescription: {
    marginTop: "20px"
  },
  developerDescriptionLabel: {
    marginTop: "10px",
  },
  developerDescriptionContent: {
    "& p": {
      marginBottom: "5px"
    }
  }
});

const InvisibleMountedKey = "InvisibleMountedKey"
const VisibleMountedKey   = "VisibleMountedKey"

const DownloadableInfoTab = forwardRef((props, ref) => {

  const {
    classes,
    downloadable = {},
    validations = {},
    scrollToElement,
    onItemChanged,
    visible,
    errorData
  } = props;

  const [tabKey, setTabKey] = useState(visible ? VisibleMountedKey : InvisibleMountedKey);

  const formFields = DownloadableModel.formFields({
    item: downloadable,
    validations: validations
  });

  const mainInfoFields = ["name", "headline", "content", "sendEmail"];
  const mainInfoFieldsRef = useRef();

  const documentsFields = ["documents"];
  const documentsFieldsRef = useRef();

  const allFields = [
    ...mainInfoFields,
    ...documentsFields
  ];

  const fieldsGroupRefs = [
    mainInfoFieldsRef,
    documentsFieldsRef
  ]

  const allFieldsRefs = useRef();

  useEffect(() => {
    allFieldsRefs.current = {};
    fieldsGroupRefs.forEach((fieldsGroup) => {
      const current = fieldsGroup.current || {};
      const refs = current.fieldRefs;
      if(refs) {
        allFieldsRefs.current = {
          ...allFieldsRefs.current,
          ...refs
        }
      }
    })
  }, [allFieldsRefs, fieldsGroupRefs])

  useEffect(() => {
    if(!errorData) {
      return;
    }

    let { fields: fieldsErrorData = {} } = errorData;

    formFields.forEach(field => {
      if(field.type !== "nestedModelsArray" && field.type !== "nestedModel") {
        return;
      }
      if(!errorData[field.name]) {
        return;
      }
      fieldsErrorData[field.name] = errorData[field.name];
    })

    let firstErrorFieldRef;
    allFields.forEach((fieldName) => {
      if(firstErrorFieldRef) {
        return;
      }
      let fieldErrorInfo = fieldsErrorData[fieldName];
      let fieldRef       = allFieldsRefs.current[fieldName];

      if(fieldErrorInfo && fieldRef) {
        firstErrorFieldRef = fieldRef
      }
    })
    if(!firstErrorFieldRef) {
      return;
    }

    if(scrollToElement) {
      scrollToElement(firstErrorFieldRef)
    }

  }, [errorData]);

  useEffect(() => {
    if(tabKey === VisibleMountedKey) {
      return;
    }

    if(visible && tabKey === InvisibleMountedKey) {
      setTabKey(VisibleMountedKey);
    }
  }, [visible]);

  return (
    <NavigationTabContent visible = { props.visible }
                          key = { tabKey } >
      <Card className = { clsx(classes.pageData, classes.pageDataCard) } >
        <CardContent className = { classes.pageDataContent } >
          <div className = { classes.cardTitle } >
            { I18n.t("model.downloadables.downloadable.field_group.main_info")}
          </div>
          <FormFields formFields = { formFields.filter(field => mainInfoFields.includes(field.name)) }
                      item = { itemWithKeys(downloadable, mainInfoFields) }
                      errorData = { errorData }
                      onItemChange = { onItemChanged }
                      defaultWeight = { 6 }
                      ref = { mainInfoFieldsRef }
                      />
        </CardContent>
      </Card>

      <Card className = { clsx(classes.pageData, classes.pageDataCard) } >
        <CardContent className = { classes.pageDataContent } >
          <div className = { classes.cardTitle } >
            { I18n.t("model.downloadables.downloadable.field_group.documents")}
          </div>
          <FormFields formFields = { formFields.filter(field => documentsFields.includes(field.name)) }
                      item = { itemWithKeys(downloadable, documentsFields) }
                      errorData = { errorData }
                      onItemChange = { onItemChanged }
                      defaultWeight = { 6 }
                      ref = { documentsFieldsRef }
                      />
        </CardContent>
      </Card>
    </NavigationTabContent>
  );
});

DownloadableInfoTab.defaultProps = {
}

export default compose(
  withScrollListener,
  withStyles(
    (theme) => ({
      ...pageStyles(theme),
      ...infoPageStyles(theme),
      ...styles(theme),
    }),
    { withTheme: true }
  )
)(DownloadableInfoTab);
